import { IRawStyle } from '@fluentui/react';
import { ReactNode } from 'react';

import { IMessageBarProps } from '../components/MessageBar/MessageBar';

// Add all used icons. All Icon List: https://developer.microsoft.com/en-us/fluentui#/styles/web/icons
export enum IconName {
  Accounts = 'Accounts',
  AlertSolid = 'AlertSolid',
  AllApps = 'AllApps',
  AppIconDefaultList = 'AppIconDefaultList',
  ArrowUpRight = 'ArrowUpRight',
  Back = 'Back',
  Blocked2 = 'Blocked2',
  Book = 'Book',
  BookAnswers = 'BookAnswers',
  Calendar = 'Calendar',
  Cancel = 'Cancel',
  CaretSolidDown = 'CaretSolidDown',
  CheckMark = 'CheckMark',
  ChevronDown = 'ChevronDown',
  ChevronLeft = 'ChevronLeft',
  ChevronLeftSmall = 'ChevronLeftSmall',
  ChevronRight = 'ChevronRight',
  ChevronRightSmall = 'ChevronRightSmall',
  CircleFill = 'CircleFill',
  CirclePause = 'CirclePause',
  Cloud = 'Cloud',
  CloudUpload = 'CloudUpload',
  Cloudy = 'Cloudy',
  Code = 'Code',
  Contact = 'Contact',
  Copy = 'Copy',
  CompletedSolid = 'CompletedSolid',
  Delete = 'Delete',
  Documentation = 'Documentation',
  Download = 'Download',
  DownloadCircle = 'DrillDown',
  Error = 'Error',
  Forward = 'Forward',
  FabricFolder = 'FabricFolder',
  GlobalNavButton = 'GlobalNavButton',
  Group = 'Group',
  Heart = 'Heart',
  HeartFill = 'HeartFill',
  Help = 'Help',
  Info = 'Info',
  InfoSolid = 'InfoSolid',
  LikeSolid = 'LikeSolid',
  Lock = 'Lock',
  Mail = 'Mail',
  More = 'More',
  MSNVideos = 'MSNVideos',
  MSNVideosSolid = 'MSNVideosSolid',
  Pinned = 'Pinned',
  PinnedSolid = 'PinnedSolid',
  Pause = 'Pause',
  Play = 'Play',
  Redeploy = 'Redeploy',
  Settings = 'Settings',
  SignOut = 'SignOut',
  Shield = 'Shield',
  SkypeCircleCheck = 'SkypeCircleCheck',
  Sort = 'Sort',
  Stack = 'Stack',
  StatusCircleErrorX = 'StatusCircleErrorX',
  StatusCircleCheckmark = 'StatusCircleCheckmark',
  TestUserSolid = 'TestUserSolid',
  TextBox = 'TextBox',
  TextDocument = 'TextDocument',
  Unknown = 'Unknown',
  WarningSolid = 'WarningSolid',
  Zoom = 'Zoom',
}

export enum KeyboardKey {
  Down = 'Down', // IE
  ArrowDown = 'ArrowDown',
  Up = 'Up', // IE
  ArrowUp = 'ArrowUp',
  Left = 'Left', // IE
  ArrowLeft = 'ArrowLeft',
  Right = 'Right', // IE
  ArrowRight = 'ArrowRight',
  Enter = 'Enter',
  Esc = 'Esc', // IE
  Escape = 'Escape',
  Tab = 'Tab',
}

export enum MessageType {
  success = 'success',
  info = 'info',
  warning = 'warning',
  blocked = 'blocked',
  severe = 'severe',
  error = 'error',
}

export interface IH2OPalette {
  // from Style Guide + UI Design, All the colors in UI should be here

  primary100: string;
  primary200: string;
  primary300: string;
  primary400: string;
  primary500: string; // <-- primary brand color used in logo
  primary600: string;
  primary700: string;
  primary800: string;
  primary900: string;

  // grays
  white: string;
  gray200: string;
  gray300: string;
  gray350: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray750: string;
  gray800: string;
  gray900: string;
  black: string;

  // transparent grays. naming convention: colorName + _a_ + alpha value. e.g gray900_a_60 would be gray900 at 0.60 alpha;
  gray400_a_16: string;
  gray900_a_60: string;
  black_a_4: string;
  black_a_8: string;
  gray300_a_50: string;
  gray300_a_16: string;

  // brand yellow
  yellow100: string;
  yellow200: string;
  yellow300: string;
  yellow400: string;
  yellow500: string; // <-- primary brand color used in logo
  yellow600: string;
  yellow700: string;
  yellow800: string;
  yellow900: string;

  // danger red
  red100: string;
  red200: string;
  red300: string;
  red400: string;
  red500: string;
  red600: string;
  red700: string;
  red800: string;
  red900: string;

  // success green
  green100: string;
  green200: string;
  green300: string;
  green400: string;
  green500: string;
  green600: string;
  green700: string;
  green800: string;
  green900: string;

  // info blue
  blue100: string;
  blue200: string;
  blue300: string;
  blue400: string;
  blue500: string;
  blue600: string;
  blue700: string;
  blue800: string;
  blue900: string;

  transparent: string;
  textOverPrimary: string;
}

/**
 * This is from Fluent Semantic Color rules https://github.com/microsoft/fluentui/blob/master/packages/theme/src/types/IH2OSemanticColors.ts
 *
 * #### Naming Convention
 *
 * The name of a semantic slot can quickly tell you how it’s meant to be used. It generally follows this format:
 *
 * `[category name][element name][Checked][Hovered/Pressed/Disabled state]`
 *
 * * `[category name]` – The “family” that this slot belongs to.
 * * `[element name]` – The name of the thing being targeted, such as the background or border.
 * * `[Checked]` – Whether the thing is checked. We assume things are unchecked by default, so no need to specify the
 * unchecked state. (“Checked” refers to anything that is on, selected, toggled, highlighted, emphasized, etc.)
 * * `[Hovered/Pressed/Disabled state]` – One of these states, if applicable. Each of these states are mutually
 * exclusive. Pressed styles overwrite hovered styles, and disabled elements cannot be hovered or pressed.
 *
 * #### Base Slots
 *
 * A basic set of slots that provide many default body styles, such as text, subtext, disabled colors, and so on.
 * If a category doesn't provide the slot you're looking for, use one from this category.
 * For example, the placeholder text on a text input field has no corresponding slot in its category,
 * so you'd use the bodySubtextColor from this category.
 *
 * #### Invariants
 *
 * When color has meaning, we do not want to change the color much theme to theme. For example, we
 * will always want errors to be some shade of red, but we will need to tweak the exact shade so it's
 * legible depending on whether it's an inverted theme or not.
 * Invariant colors should almost never be changed by the theme, the defaults should suffice.
 *
 * #### Components slots
 */

export interface IH2OSemanticColors {
  bodyBackground: string;
  contentBackground: string;
  bodyFrameBackground: string;
  textPrimary: string;
  textSecondary: string;
  textQuaternary: string;
  textQuinary: string;
  textDisabled: string;

  //// Invariants - slots that rarely change color theme-to-theme because the color has meaning

  //// Components
  // Link
  linkText: string;
  linkOverPrimary: string;
  // Footer
  footerBackground: string;
  // Button
  // default button - aka secondary button
  buttonText: string;
  buttonDefaultSecondaryText: string;
  buttonBorder: string;
  buttonHoverBackground: string;
  buttonHoverText: string;
  buttonHoverBorder: string;
  buttonActiveBorder: string;
  buttonActiveBackground: string;
  buttonDisabledBackground: string;
  buttonDisabledBorderColor: string;

  buttonPrimaryText: string;
  buttonPrimaryBackground: string;
  buttonPrimaryHoverBackground: string;
  buttonPrimaryActiveBackground: string;
  buttonPrimaryActiveBorder: string;
  buttonPrimaryDisabledBackground: string;
  buttonPrimaryDisabledBorder: string;
  buttonPrimaryDisabledText: string;

  buttonSecondaryText: string;
  buttonSecondaryBackground: string;
  buttonSecondaryHoverBackground: string;
  buttonSecondaryActiveBackground: string;
  buttonSecondaryActiveBorder: string;
  buttonSecondaryDisabledBackground: string;
  buttonSecondaryDisabledBorder: string;
  buttonSecondaryDisabledText: string;

  buttonGhostText: string;
  buttonGhostBackground: string;
  buttonGhostBorder: string;
  buttonGhostBackgroundHovered: string;
  buttonGhostBorderHovered: string;
  buttonGhostTextHovered: string;
  buttonGhostBackgroundFocused: string;
  buttonGhostTextFocused: string;
  buttonGhostBorderFocused: string;
  buttonGhostBackgroundActive: string;
  buttonGhostBorderActive: string;
  buttonGhostTextActive: string;
  buttonGhostBackgroundDisabled: string;
  buttonGhostBorderDisabled: string;
  buttonGhostTextDisabled: string;

  buttonDanger: string;
  buttonDangerHovered: string;
  buttonDangerActiveBorder: string;
  buttonDangerDisabled: string;

  buttonStealthText: string;
  buttonStealthBackground: string;
  buttonStealthHoverBackground: string;
  buttonStealthActiveBackground: string;
  buttonStealthActiveBorder: string;
  buttonStealthDisabledBackground: string;

  buttonIconColor: string;
  buttonDividerBackground: string;
  buttonIconColumn: string;
  buttonIconColumnHovered: string;
  buttonLinkText: string;
  buttonLinkIcon: string;
  buttonLinkHoverText: string;
  buttonLinkUnderlineText: string;
  buttonLinkUnderlineHoverText: string;
  buttonCarouselIcon: string;

  buttonInlineBackground: string;
  buttonInlineBorder: string;
  buttonInlineHoverBackground: string;
  buttonInlineHoverText: string;
  buttonInlineSuccess: string;

  buttonCarouselBackground: string;
  buttonCarouselBackgroundHovered: string;

  // Form Fields
  inputBorder: string;
  inputText: string;
  inputReadOnlyText: string;
  inputLabel: string;
  inputPlaceholder: string;
  inputActiveBorder: string;
  inputErrorBorder: string;
  inputErrorMessageText: string;
  inputSuccessBorder: string;
  inputSuccessMessageText: string;
  inputDisabledBackground: string;
  inputDisabledNoBorder: string;
  inputDisabledBorder: string;
  inputListItemHoveredBackground: string;
  inputListItemActiveBackground: string;

  dropdownInlineBorder: string;

  fileUploadActiveBorder: string;

  checkboxCheckMark: string;
  checkboxCheckMarkErr: string;

  // Message bar
  messageBarTitleText: string;
  messageBarContentText: string;
  messageBarContentTextSuccess: string;
  messageBarContentTextInfo: string;
  messageBarContentTextError: string;
  messageBarContentTextWarning: string;
  messageBarIconSuccess: string;
  messageBarBackgroundSuccess: string;
  messageBarIconInfo: string;
  messageBarBackgroundInfo: string;
  messageBarIconError: string;
  messageBarBackgroundError: string;
  messageBarIconWarning: string;
  messageBarBackgroundWarning: string;
  // loader
  loaderBarIndicator: string;
  loaderBarIndicatorButton: string;
  loaderBarIndicatorError: string;
  loaderBarBackground: string;
  loaderIndicator: string;
  loader: string;
  loaderBarLabel: string;
  loaderBarDescription: string;
  loaderLabel: string;

  // tag & category
  categoryBackground: string;
  categoryText: string;
  tagBackground: string;
  tagText: string;
  tagBorderLeft: string;
  // tooltip
  tooltipBackground: string;
  tooltipText: string;
  // nav
  navText: string;
  navSelectedBackground: string;
  // etc
  liked: string;
}

export interface IH2OTheme {
  defaultFontStyle?: IRawStyle;
  palette?: Partial<IH2OPalette>;
  semanticColors?: Partial<IH2OSemanticColors> | { [key: string]: string }; // Project can have more semantic colors
}

export type IStyleFunctionOrObject<S> = Partial<S> | ((theme: IH2OTheme) => Partial<S>);

export interface IComponent<S> {
  styles?: IStyleFunctionOrObject<S> | IStyleFunctionOrObject<S>[];
  'data-test'?: string;
}

export interface SortInfo<T extends { [key: string]: any }> {
  fieldName: keyof T;
  isSorted?: boolean;
  isSortedDescending?: boolean;
}

export interface IMessageBarMessage extends Omit<IMessageBarProps, 'onClick' | 'onMouseEnter'> {
  id?: string;
  groupId?: string;
  message: ReactNode;
  hideDismissButton?: boolean;
  data?: { [key: string]: any };
  onClick?: (e: React.MouseEvent, message: IMessageBarMessage) => void;
  onMouseEnter?: (e: React.MouseEvent, message: IMessageBarMessage) => void;
}

export enum MessageBarListType {
  notification = 'notification',
}

export type MessageAction =
  | { type: MessageActionType.Add; payload: IMessageBarMessage }
  | { type: MessageActionType.AddMessages; payload: IMessageBarMessage[] }
  | { type: MessageActionType.Read; payload: ReactNode }
  | { type: MessageActionType.ReadById; payload: string | undefined }
  | { type: MessageActionType.Remove; payload: ReactNode }
  | { type: MessageActionType.RemoveById; payload: string | undefined }
  | { type: MessageActionType.RemoveGroup; payload: string }
  | { type: MessageActionType.RemoveAll };

export enum MessageActionType {
  Add = 'Add',
  AddMessages = 'AddMessages',
  Remove = 'Remove',
  RemoveById = 'RemoveById',
  RemoveGroup = 'RemoveGroup',
  RemoveAll = 'RemoveAll',
  Read = 'Read',
  ReadById = 'ReadById',
}

export interface ColumnMeta {
  headerFieldName?: string;
}

export enum VizType {
  BAR,
  AREA,
}

export enum Location {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  CENTER = 'CENTER',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

export enum ChatMessageAction {
  addUserMessage = 'Add User Message',
  clickCard = 'Click Card',
  clickButton = 'Click Button',
  like = 'Like',
  dislike = 'Dislike',
  changeOption = 'changeOption',
}

export interface MinMax {
  min: number;
  max: number;
}

export interface User {
  id: string;
  name?: string;
  data?: { [key: string]: any };
}

export interface IBaseChartProps {
  data?: { [key: string]: any }[];
  labelFieldName?: string;
  valueFieldNames?: string[];
}
